import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { getMinPxFromElement, memoChecker } from '../../../utils/globals/components'
import { TwinDictionary } from '../../../utils/globals/dictionary'
import { TwinAutoSizer } from '../Adapters'
import { AllSearchableComponents, ColumnConfiguration, HeaderColumnProps } from '../VirtualTableListing/Subcomponents/types'
import { calculateColumnWidth, cleanVTColumnsForMemo, getVirtualizedListingColumnWidth } from '../VirtualTableListing/functions'
import { TableWithCustomVirtualListingRowRenderer, TableWithCustomVirtualListingRowProps, TableWithCustomVirtualListingContainerRenderer } from './types'
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons'
import { TwinIconToolTip } from '../../TwinIcon'
import { ChangeColumnConfigTy } from '../VirtualTableListing/types'
import { flushSync } from 'react-dom'
import { twinFetchPostJSON } from '../../../utils/globals/data'
import { SelectedColumnsTy, SelectedColumnsAndFilters } from '../../../utils/hooks/useConfigColumns'
import { changeEmployeeParams } from '../../../utils/reducers/reduxDispatch'
import { HeaderSearchable } from '../VirtualTableListing/Subcomponents'
import { parseDefaultColumnSelectedFilterValue } from '../VirtualTableListing/Subcomponents/functions'
import { HeaderChangesTy } from '../types'
import { OrderLayoutTableFilter } from '../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/useOrderLayoutTableLogic/types'
import './tableWithCustomVirtualListing.sass'

export interface TableWithCustomVirtualListingPropsBase extends TableWithCustomVirtualListingAutosizedLogicProps {
    data: TwinDictionary[] | null
    columns: ColumnConfiguration[]
    rowRenderer?: TableWithCustomVirtualListingRowRenderer
    containerRenderer?: TableWithCustomVirtualListingContainerRenderer
    updateEmployeeParameter?: boolean
    defaultMinWidthColumns?: number
    rowHeight?: number
}

interface TableWithCustomVirtualListingProps extends TableWithCustomVirtualListingPropsBase { 
    disableScroll?: boolean
    className?: string
    headerChanges?: HeaderChangesTy
    filters?: OrderLayoutTableFilter
    getDataFnc?: (rowIndex: number) => TwinDictionary
}

const TableWithCustomVirtualListing: React.FC<TableWithCustomVirtualListingProps> = ({ columns, data, rowRenderer, className, containerRenderer, disableScroll  = false, ...rest }) => {
    return (
        <div className={'table_with_custom_virtual flex-auto overflow-x-auto overflow-y-hidden h-full '+ (className ||'')}>
            <TwinAutoSizer disableHeight={disableScroll}>
                {({ width, height }) => (
                    <TableWithCustomVirtualListingAutosized width={width} height={height} columns={columns} data={data} rowRenderer={rowRenderer} containerRenderer={containerRenderer} {...rest }/>
                )}
            </TwinAutoSizer>
        </div>
    )
}
const DEFAULT_ROWHEIGHT_PX = 48

interface TableWithCustomVirtualListingAutosizedProps extends TableWithCustomVirtualListingPropsBase {
    width: number
    height: number
    headerChanges?: HeaderChangesTy
    filters?: OrderLayoutTableFilter
    getDataFnc?: (rowIndex: number)=>TwinDictionary
}

const TableWithCustomVirtualListingAutosized: React.FC<TableWithCustomVirtualListingAutosizedProps> = ({ width, height, data, columns, defaultMinWidthColumns = 100, rowRenderer, containerRenderer, updateEmployeeParameter, rowHeight = DEFAULT_ROWHEIGHT_PX, headerChanges, filters, getDataFnc, ...logic }) => {
    const widthCalcul = calculateColumnWidth(width, columns, defaultMinWidthColumns)
    const scrollable = widthCalcul.newWidth !== width
    const memorizedColumns: TwinDictionary = useMemo(() => {
        let myDictWidth: TwinDictionary = {}
        for (const column of columns) {
            const width = getVirtualizedListingColumnWidth(column, widthCalcul.columnWidth, scrollable, defaultMinWidthColumns)
            myDictWidth[column.id] = {
                flex: `0 1 ${width}px`,
                minWidth: column.width ? column.width : (width < defaultMinWidthColumns) ? defaultMinWidthColumns : width + 'px',
            }
        }
        return { dictWidthColumns: myDictWidth, columns}
    }, [columns, defaultMinWidthColumns, scrollable, widthCalcul.columnWidth])

    const memoData = useMemo(() => data, [data])
    const memoFilters = useMemo(() => filters, [filters])
    const memoHeight = useMemo(() => (data?.length || 0) * rowHeight, [data, rowHeight])
    const styles = useMemo(() => {
        return {
            rowStyle:  {
                width: widthCalcul.newWidth + 'px',
                maxWidth: widthCalcul.newWidth + 'px',
                position: 'absolute'
            },
            containerStyle: {
                width: widthCalcul.newWidth + 'px',
                maxWidth: widthCalcul.newWidth + 'px',
                height: memoHeight + 'px',
                position: 'relative'
            }
        }
    }, [widthCalcul.newWidth, memoHeight])


    const { ref, tableNode, changeColumnConfig, rowsVisible, startIndex } = useTableWithCustomVirtualListingAutosizedLogic({ ...logic, rowHeight })

    const myRowRenderer = useCallback((startIndex: number) => {
        const renderRows: JSX.Element[] = []
        if (memoData) {
            let myStartIndex = startIndex - 20 < 0 ? 0 : startIndex - 20
            const visibleData = memoData.slice(myStartIndex, startIndex + rowsVisible);
            for (const rowIndex in visibleData) {
                const myRowIndex = myStartIndex + parseInt(rowIndex)
                let mystyles: any = {...styles.rowStyle}
                mystyles.top = (myRowIndex) * rowHeight
                const props: TableWithCustomVirtualListingRowProps = {
                    style: mystyles,
                    columns: memorizedColumns.columns,
                    rowData: getDataFnc ? getDataFnc(myRowIndex) : visibleData[rowIndex],
                    dictWidthColumns: memorizedColumns.dictWidthColumns,
                    rowIndex: myRowIndex
                }
                if (rowRenderer) {
                    props.key = props.rowData?.id || myRowIndex
                    renderRows.push(rowRenderer(props))
                } else {
                    renderRows.push(<TableWithoutDefaultRowRenderer {...props} key={props.rowData?.id || myRowIndex} />)
                }
            }
        }
        return renderRows
    }, [memorizedColumns, memoData, rowRenderer, styles.rowStyle, rowsVisible, rowHeight, getDataFnc])



    const containerStyle = {
        width: width + 'px',
        height: (height - 50) + 'px',
    }

    const rendered = myRowRenderer(startIndex)

    let myContainer = <div className='data_rows h-full' style={styles.containerStyle as any}>{rendered}</div>
    if (containerRenderer){
        myContainer = containerRenderer({ children: rendered, style: styles.containerStyle as any})
    }
    return (
        <div className={'table_with_custom_virtual_inner ' + (scrollable ? ' scrollable' : '')} >
            <RowHeader tableNode={tableNode} style={{ width: width + 'px' }} columns={memorizedColumns.columns} {...{ defaultMinWidthColumns, updateEmployeeParameter, changeColumnConfig, dictWidthColumns: memorizedColumns.dictWidthColumns, headerChanges, filters: memoFilters }} />
            <div ref={ref} className='table_grid_rows overflow-y-auto' style={containerStyle}>
                {myContainer}
            </div>
        </div>
    )
}

interface TableWithCustomVirtualListingAutosizedLogicProps {
    name?: string
    setSelectedColumnsAndFilters?: React.Dispatch<React.SetStateAction<SelectedColumnsAndFilters>>
    selectedFilterId?: number
    rowHeight?: number
}

const useTableWithCustomVirtualListingAutosizedLogic = ({ name, setSelectedColumnsAndFilters, selectedFilterId, rowHeight = DEFAULT_ROWHEIGHT_PX }: TableWithCustomVirtualListingAutosizedLogicProps) => {
    const ref = useRef<any>(null)
    const [startIndex, setStartIndex] = useState(0)
    const [rowsVisible, setRowsVisible] = useState(0)
    const tableNode = ref.current

    useEffect(() => {
        const listener = (event: any) => {
            const scrollTop = ref.current.scrollTop;
            const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight));
            const target = event.target
            const header = target?.parentElement?.querySelector('.row_header')
            if (target.scrollLeft !== header.scrollLeft) {
                header.scrollLeft = target.scrollLeft
            }
            setStartIndex(startIndex)
        };
        if (tableNode) {
            tableNode.addEventListener("scroll", listener);
            if (ref.current.clientHeight){
                setRowsVisible(Math.ceil(ref.current.clientHeight / rowHeight) + 20)
            }
        }
        return () => {
            if (tableNode) {
                tableNode.removeEventListener("scroll", listener);
            }
        };
    }, [tableNode, rowHeight])

    const updateFilterOnTable = useCallback(async (mycolumns: SelectedColumnsTy, id: number) => {
        delete mycolumns.id
        delete mycolumns.name
        const myValue = JSON.stringify(mycolumns)
        await twinFetchPostJSON('/api/app/config/filter/updateFilter', { id, value: myValue })
    }, [])

    const updateEmployeeParams = useCallback(async (mycolumns: SelectedColumnsTy) => {
        const myName = 'table_' + name
        const myValue = JSON.stringify(mycolumns)
        await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: myName, value: myValue })
        changeEmployeeParams({ [myName]: myValue })
    }, [name])

    const changeColumnConfig: ChangeColumnConfigTy = useCallback(async (id, userConfig, updateDb = true) => {
        await flushSync(async () => {
            let mySelectedColumns = {}
            if(name && setSelectedColumnsAndFilters){
                await setSelectedColumnsAndFilters((old) => {
                    const copyOld: SelectedColumnsAndFilters = JSON.parse(JSON.stringify(old))
                    copyOld.selectedColumns[id] = { ...userConfig }
                    mySelectedColumns = copyOld
                    return copyOld
                })
                if (updateDb) {
                    if (selectedFilterId) {
                        await updateFilterOnTable(mySelectedColumns, selectedFilterId)
                    } else {
                        await updateEmployeeParams(mySelectedColumns)
                    }
                }
            }
        })
    }, [setSelectedColumnsAndFilters, updateEmployeeParams, selectedFilterId, updateFilterOnTable, name])

    return { ref, tableNode, changeColumnConfig, startIndex, rowsVisible }
}


interface RowHeaderProps {
    style?: React.CSSProperties
    dictWidthColumns: TwinDictionary
    columns: ColumnConfiguration[]
    tableNode: null | HTMLElement
    defaultMinWidthColumns: number
    updateEmployeeParameter?: boolean
    changeColumnConfig: ChangeColumnConfigTy
    headerChanges?: HeaderChangesTy
    filters?: OrderLayoutTableFilter
}

const RowHeader: React.FC<RowHeaderProps> = memo(({ style, dictWidthColumns, columns, tableNode, defaultMinWidthColumns, updateEmployeeParameter, changeColumnConfig, headerChanges, filters }) => {
    const rowColumns: JSX.Element[] = []

    useLayoutEffect(() => {
        if (tableNode) {
            const header = tableNode.parentElement?.querySelector('.row_header') as any
            if (header) {
                const maxScrollLeftHeader = header.scrollWidth - header.clientWidth;
                const maxScrollLeftTable = tableNode.scrollWidth - tableNode.clientWidth;
                if (maxScrollLeftHeader !== maxScrollLeftTable) {
                    const diff = maxScrollLeftTable - maxScrollLeftHeader
                    const padding = header?.style?.paddingRight.substring(0, header?.style?.paddingRight?.indexOf('px')) || 0
                    header.style.paddingRight = parseFloat(String(padding)) + diff + 'px'
                }
            }
        }
    }, [tableNode])
    for (const column of columns) {
        if (column.havePermission !== false && column.haveModule !== false) {
            rowColumns.push(<ColumnHeader key={'header' + column.id} style={dictWidthColumns[column.id]} column={column} {...{ id: column.id, width: column.width, minWidth: column.minWidth, changeColumnConfig, defaultMinWidthColumns, updateEmployeeParameter, searchableComponent: column.searchableComponent, headerChanges}} columnSelectedFilter={filters?.singleFieldsSearch?.[column.id]}  />)
        }
    }

    return (<div className={`row_header flex px-8 border-b border-gray-F7 bg-gray-F7 overflow-hidden`} style={style}>{rowColumns}</div>)
}, (oldProps, nextProps) => memoChecker(oldProps, nextProps, ['columns', 'style', 'filters'], { 'columns': ['props', '_owner', 'label'] }, { 'label': cleanVTColumnsForMemo }))

interface ColumnHeaderProps extends ColumnHeaderLogicProps {
    style?: React.CSSProperties
    column: ColumnConfiguration
    width?: number
    searchableComponent?: AllSearchableComponents
    headerChanges?: HeaderChangesTy
    columnSelectedFilter?: any
}

const ColumnHeader: React.FC<ColumnHeaderProps> = ({ style, column, width, updateEmployeeParameter, searchableComponent, headerChanges, columnSelectedFilter,  ...rest }) => {
    const { setRef} = useColumnHeaderLogic({...rest, updateEmployeeParameter})
    const filterValue = parseDefaultColumnSelectedFilterValue(columnSelectedFilter)
    return (<div className={'header_column relative flex px-8 py-12 font-medium items-center h-50 ' + (column.headerClassName || '')} style={style}>
        {!column.label || typeof column.label === 'string' ? column.label : column.label({})}
        {!column.toolTip ? '' : typeof column.toolTip === 'string' ? <TwinIconToolTip positionStrategy='fixed' className='ml-6' icon={faInfoCircle} message={column.toolTip} id={column.id+'tooltip'} classNameTooltip='max-w-300' /> : column.toolTip({})}
        <HeaderSearchable {...{ searchableComponent, dataKey: rest.id, value: filterValue }} changeSingleField={headerChanges?.changeSingleField} />
        {!width && updateEmployeeParameter ? <div className='table_header_separator cursor-col-resize' ref={setRef}></div> : null}
    </div>)
}

interface ColumnHeaderLogicProps {
    changeColumnConfig: ChangeColumnConfigTy
    defaultMinWidthColumns: number
    minWidth?: HeaderColumnProps['minWidth']
    id: HeaderColumnProps['id']
    updateEmployeeParameter?: boolean
}

const useColumnHeaderLogic = ({ minWidth, defaultMinWidthColumns, changeColumnConfig, id, updateEmployeeParameter }: ColumnHeaderLogicProps) => {
    const [ref, setRef] = useState<any | null>(null)
    useEffect(() => {
        let movement = { starterPx: 0, starterWidth: 0 }
        const mouseUp = (event: any) => {
            document.querySelector('body')?.classList.remove('cursor-col-resize')
            document.removeEventListener('mousemove', mouseMove)
            let myWidth = movement.starterWidth + (event.clientX - movement.starterPx)
            const myMinWidth = minWidth || defaultMinWidthColumns
            if (myWidth < myMinWidth) {
                myWidth = myMinWidth
            }
            changeColumnConfig(id, { userWidth: myWidth }, updateEmployeeParameter)
            document.removeEventListener('mouseup', mouseUp)
        }
        const mouseMove = (event: any) => {
            const headerElement = ref?.parentElement?.parentElement?.parentElement
            if (headerElement) {
                let myWidth = movement.starterWidth + (event.clientX - movement.starterPx)
                const myMinWidth = minWidth || defaultMinWidthColumns
                if (myWidth < myMinWidth) {
                    myWidth = myMinWidth
                }
                changeColumnConfig(id, { userWidth: myWidth }, false)
            }
        }
        const mouseDown = (event: any) => {
            const target = event.target
            const headerElement = target?.parentElement
            if (headerElement && headerElement.classList.contains('header_column')) {
                movement = { starterPx: event.clientX, starterWidth: getMinPxFromElement(headerElement) }
            }
            document.querySelector('body')?.classList.add('cursor-col-resize')
            document.addEventListener('mousemove', mouseMove)
            document.addEventListener('mouseup', mouseUp)
        };
        if (ref) {
            ref.addEventListener("mousedown", mouseDown);
        }
        return () => {
            if (ref) {
                ref.removeEventListener("mousedown", mouseDown);
                document.removeEventListener('mousemove', mouseMove)
                document.removeEventListener('mouseup', mouseUp)
            }
        };
    }, [ref, id, minWidth, changeColumnConfig, defaultMinWidthColumns, updateEmployeeParameter])

    return { ref, setRef }
}

export const TableWithoutDefaultRowRenderer: React.FC<TableWithCustomVirtualListingRowProps> = ({ style, className, rowData, dictWidthColumns, rowIndex, columns }) => {
    const rowColumns: JSX.Element[] = []
    for (const column of columns) {
        if (column.havePermission !== false && column.haveModule !== false) {
            rowColumns.push(<Column key={rowIndex + column.id} style={dictWidthColumns[column.id]} rowData={rowData} rowIndex={rowIndex} column={column} className={column.className} />)
        }
    }
    return (<div className={'table_row flex px-8 border-b border-gray-F7 overflow-hidden cursor-pointer ' + (className || '')} style={style}>{rowColumns}</div>)
}

interface ColumnProps {
    style?: React.CSSProperties
    className?: string
    rowData: TwinDictionary
    rowIndex: number
    column: ColumnConfiguration
}

const Column: React.FC<ColumnProps> = ({ style, className, rowData, rowIndex, column }) => {
    const result = rowData[column.dataKey]
    let columnResult = result

    if (column.parseData) {
        columnResult = column.parseData?.(result)
    }
    if (column.customRender) {
        columnResult = column.customRender(result, { ...rowData, rowIndex })
    }

    return (<div className={'table_column flex px-8 py-12 overflow-hidden items-center ' + (className || '')} style={style}>{columnResult}</div>)
}

export default TableWithCustomVirtualListing